import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PhoneCall, PhoneOff } from "lucide-react";
import { useConversation } from "@elevenlabs/react";
import { toast } from "@/hooks/use-toast";
import { useVoiceClientTools } from "@/hooks/useVoiceClientTools";
import {
  formatVoiceSessionError,
  logVoiceSessionDebug,
  startVoiceAgentSession,
} from "@/lib/voice-session";
import AudioBars from "./AudioBars";
import VoiceTranscriptBubble, { VoiceChatMessage } from "./VoiceTranscriptBubble";

const AGENT_ID = "agent_9601kp8f265cf14tt9yksavr6wq2";
const VOICE_DEBUG_CONTEXT = "VoiceWidget";

const VoiceWidget: React.FC = () => {
  const { t } = useTranslation();
  const [isConnecting, setIsConnecting] = useState(false);
  const [messages, setMessages] = useState<VoiceChatMessage[]>([]);
  const [showTranscript, setShowTranscript] = useState(true);
  const clientTools = useVoiceClientTools();

  const pushMessage = useCallback((role: "user" | "agent", text: string) => {
    if (!text || !text.trim()) return;
    setMessages((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, role, text: text.trim() },
    ]);
  }, []);

  const conversation = useConversation({
    clientTools,
    onConnect: () => {
      logVoiceSessionDebug("conversation_connected", { component: VOICE_DEBUG_CONTEXT });
    },
    onDisconnect: () => {
      logVoiceSessionDebug("conversation_disconnected", { component: VOICE_DEBUG_CONTEXT });
      setIsConnecting(false);
    },
    onMessage: (message: unknown) => {
      const m = message as {
        type?: string;
        source?: "user" | "ai" | "agent";
        message?: string;
        text?: string;
        user_transcription_event?: { user_transcript?: string };
        agent_response_event?: { agent_response?: string };
        agent_response_correction_event?: { corrected_agent_response?: string };
      };

      const type = m?.type;
      if (m?.source && (m.message || m.text)) {
        const role = m.source === "user" ? "user" : "agent";
        pushMessage(role, m.message || m.text || "");
        return;
      }
      if (type === "user_transcript" && m.user_transcription_event?.user_transcript) {
        pushMessage("user", m.user_transcription_event.user_transcript);
        return;
      }
      if (type === "agent_response" && m.agent_response_event?.agent_response) {
        pushMessage("agent", m.agent_response_event.agent_response);
        return;
      }
      if (
        type === "agent_response_correction" &&
        m.agent_response_correction_event?.corrected_agent_response
      ) {
        pushMessage("agent", m.agent_response_correction_event.corrected_agent_response);
      }
    },
    onError: (error: unknown) => {
      console.error("Voice agent error:", error);
      logVoiceSessionDebug("conversation_error", {
        component: VOICE_DEBUG_CONTEXT,
        name: error instanceof Error ? error.name : undefined,
        message: error instanceof Error ? error.message : String(error ?? ""),
      });
      setIsConnecting(false);
      toast({
        variant: "destructive",
        title: t("cta_voice_agent"),
        description: formatVoiceSessionError(error),
      });
    },
  });

  const isActive = conversation.status === "connected";
  const isSpeaking = conversation.isSpeaking;

  useEffect(() => {
    if (isActive) setShowTranscript(true);
  }, [isActive]);

  const handleToggle = useCallback(async () => {
    if (isActive) {
      logVoiceSessionDebug("session_end_requested", { component: VOICE_DEBUG_CONTEXT });
      await conversation.endSession();
      return;
    }

    setIsConnecting(true);
    setMessages([]);
    try {
      logVoiceSessionDebug("microphone_permission_requested", {
        component: VOICE_DEBUG_CONTEXT,
        hasMediaDevices: typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia),
        isSecureContext: typeof window !== "undefined" ? window.isSecureContext : undefined,
      });
      await navigator.mediaDevices.getUserMedia({ audio: true });
      logVoiceSessionDebug("microphone_permission_granted", { component: VOICE_DEBUG_CONTEXT });
      const connectionType = await startVoiceAgentSession({ conversation, agentId: AGENT_ID });
      logVoiceSessionDebug("session_started", {
        component: VOICE_DEBUG_CONTEXT,
        connectionType,
      });
    } catch (err) {
      console.error("startSession failed:", err);
      logVoiceSessionDebug("session_start_failed", {
        component: VOICE_DEBUG_CONTEXT,
        name: err instanceof Error ? err.name : undefined,
        message: err instanceof Error ? err.message : String(err ?? ""),
      });
      toast({
        variant: "destructive",
        title: t("cta_voice_agent"),
        description: formatVoiceSessionError(err),
      });
    } finally {
      setIsConnecting(false);
    }
  }, [isActive, conversation, t]);

  const statusLabel = isConnecting
    ? "..."
    : isActive
    ? isSpeaking
      ? t("cta_voice_speaking", "Parle…")
      : t("cta_voice_listening", "Écoute…")
    : t("cta_voice_agent_short");

  return (
    <div className="fixed bottom-[200px] sm:bottom-[180px] right-4 sm:right-6 z-[60] flex flex-col items-end gap-2">
      <VoiceTranscriptBubble
        open={isActive && showTranscript}
        isSpeaking={isSpeaking}
        messages={messages}
        onClose={() => setShowTranscript(false)}
      />

      {isActive ? (
        <div className="flex items-center gap-2">
          {/* Status pill (green) — non-interactive */}
          <div
            aria-live="polite"
            className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-3 text-sm font-semibold shadow-lg ${
              isSpeaking
                ? "border-blue-500/50 bg-blue-600 text-white shadow-[0_0_0_4px_rgba(59,130,246,0.25),0_0_24px_8px_rgba(59,130,246,0.55)] animate-pulse"
                : "border-green-500/50 bg-green-600 text-white shadow-[0_0_0_3px_rgba(34,197,94,0.25),0_0_18px_4px_rgba(34,197,94,0.35)]"
            }`}
          >
            <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white shadow-sm">
              <AudioBars
                getFrequencyData={() =>
                  isSpeaking
                    ? conversation.getOutputByteFrequencyData?.()
                    : conversation.getInputByteFrequencyData?.()
                }
                bars={5}
                color="bg-white"
                heightPx={18}
                className="w-7"
              />
            </span>
            <span className="pr-1">{statusLabel}</span>
          </div>

          {/* Hangup button (red) */}
          <button
            type="button"
            onClick={handleToggle}
            disabled={isConnecting}
            aria-label={t("cta_voice_hangup", "Raccrocher")}
            className="inline-flex items-center gap-2 rounded-full border border-red-500/60 bg-red-600 px-3.5 py-3 text-sm font-semibold text-white shadow-lg shadow-red-600/30 transition-all hover:-translate-y-0.5 hover:bg-red-700"
          >
            <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white shadow-sm">
              <PhoneOff className="h-4 w-4" />
            </span>
            <span className="pr-1">{t("cta_voice_hangup", "Raccrocher")}</span>
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleToggle}
          disabled={isConnecting}
          aria-label={t("cta_voice_agent")}
          className="inline-flex items-center gap-2 rounded-full border border-accent/35 bg-foreground px-3.5 py-3 text-sm font-semibold text-background shadow-lg transition-all hover:-translate-y-0.5 hover:opacity-95"
        >
          <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-sm">
            <PhoneCall className="h-4 w-4" />
          </span>
          <span className="pr-1">{statusLabel}</span>
        </button>
      )}
    </div>
  );
};

export default VoiceWidget;
